import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from './axiosInstance';
import { memberName } from '../components/memberName';
import { clearLocalSession, saveSession, signOutBrowser } from '../browserSession';
import { API_URL } from '../apiConfig';

const AuthContext = createContext(null);

export function tokenExpiresAt(token) {
  if (token?.startsWith('browser:')) return Infinity; // Expiry is checked by the API.
  try {
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')));
    return Number.isFinite(payload.exp) ? payload.exp * 1000 : 0;
  } catch { return 0; }
}

function restoreSession() {
  const token = localStorage.getItem('token');
  if (!token) return { token: null, username: 'Racer', expired: false };
  if (tokenExpiresAt(token) <= Date.now()) {
    return { token: null, username: 'Racer', expired: true };
  }
  return { token, username: localStorage.getItem('username') || 'Racer', expired: false };
}

export function AuthProvider({ children }) {
  const cache = useQueryClient();
  const [session, setSession] = useState(restoreSession);
  const [signOutError, setSignOutError] = useState('');
  const { data: profile } = useQuery({
    queryKey: ['account', session.token], enabled: !!session.token,
    queryFn: async () => (await api.get('/api/account')).data,
  });
  useEffect(() => {
    if (session.expired && !session.token) {
      localStorage.removeItem('token'); localStorage.removeItem('username');
    }
  }, [session.expired, session.token]);
  const clearSession = useCallback((expired = false) => {
    clearLocalSession('token');
    setSession({ token: null, username: 'Racer', expired });
    cache.clear();
  }, [cache]);
  const signIn = (token, username, profile = null) => {
    cache.clear();
    if (profile) cache.setQueryData(['account', token], profile);
    saveSession('token', token, { username });
    setSession({ token, username, expired: false });
  };
  const acknowledgeSessionExpiry = useCallback(() => {
    setSession(current => ({ ...current, expired: false }));
  }, []);
  useEffect(() => {
    const expire = () => clearSession(true);
    window.addEventListener('apex:session-expired', expire);
    return () => window.removeEventListener('apex:session-expired', expire);
  }, [clearSession]);
  const signOut = async () => {
    setSignOutError('');
    try { await signOutBrowser('token', API_URL); clearSession(false); return true; }
    catch (error) { setSignOutError(error.message); return false; }
  };
  let role = profile?.role || 'USER';
  try { role = JSON.parse(atob(session.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).role || 'USER'; } catch {}
  return <AuthContext.Provider value={{ token: session.token, username: session.username, profile, displayName: memberName(profile), role,
    sessionExpired: session.expired, acknowledgeSessionExpiry, signIn, signOut, signOutError }}>{children}</AuthContext.Provider>;
}
export const useAuth = () => useContext(AuthContext);
